import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {searchApi} from './searchApi';
import {
  DocumentCategories,
  documentTypeMappings,
  ResourceTabs,
  SearchResultArtifact,
  SearchResultProcess,
  SearchResults,
} from 'app/types/Shared';
import {RootState} from 'app/store';

export class SearchResultsExtension {
  data: SearchResults;

  constructor(data: SearchResults) {
    this.data = data;
  }

  getDocumentsByType(filter: DocumentCategories): Array<SearchResultArtifact> {
    if (!this.data || !this.data.artifacts) {
      return [];
    }

    switch (filter) {
      case DocumentCategories.SOPs:
        return this.data.artifacts.filter((x) => documentTypeMappings[DocumentCategories.SOPs].includes(x.type));

      case DocumentCategories.ToolsAndForms:
        return this.data.artifacts.filter((x) =>
          documentTypeMappings[DocumentCategories.ToolsAndForms].includes(x.type),
        );

      case DocumentCategories.BusinessControlled:
        return this.data.artifacts.filter((x) =>
          documentTypeMappings[DocumentCategories.BusinessControlled].includes(x.type),
        );
      case DocumentCategories.Processes:
        return [];

      default:
        return this.data.artifacts;
    }
  }

  getProcesses(tab: ResourceTabs): Array<SearchResultProcess> {
    if (!this.data || !this.data.processes) {
      return [];
    }
    return this.data.processes?.filter((x) =>
      tab === ResourceTabs.TTDOM ? x.operating_models_list === 'TTDOM' : x.operating_models_list !== 'TTDOM',
    );
  }

  getProcessDocuments(
    filters: Array<DocumentCategories>,
    tab: ResourceTabs,
  ): Array<{process: SearchResultProcess; artifacts: SearchResultArtifact[]}> {
    const processToDocuments = new Array<{process: SearchResultProcess; artifacts: SearchResultArtifact[]}>();
    const docResults: Array<SearchResultArtifact> = [];
    if (filters?.length === 0) {
      docResults.push(...this.data.artifacts);
    } else {
      filters.forEach((filter) => {
        docResults.push(...this.getDocumentsByType(filter));
      });
    }
    this.getProcesses(tab).forEach((p) => {
      processToDocuments.push({process: p, artifacts: []});
    });
    if (filters?.length > 0 && filters.every((filter) => filter === DocumentCategories.Processes)) {
      return processToDocuments;
    }

    // iterate through artifacts and map them to processes, add if needed
    docResults?.forEach((artifact) => {
      artifact.j_process_artifact.forEach((process) => {
        const idx = processToDocuments.findIndex((p) => p.process.process_id === process.process.process_id);
        if (
          idx == -1 &&
          (tab === ResourceTabs.TTDOM
            ? process.process.operating_models_list === 'TTDOM'
            : process.process.operating_models_list !== 'TTDOM')
        ) {
          processToDocuments.push({process: process.process, artifacts: [artifact]});
        } else {
          processToDocuments[idx]?.artifacts?.push(artifact);
        }
      });
    });

    return processToDocuments;
  }
}

interface SearchState {
  query: string;
  filters: Array<DocumentCategories>;
  results: SearchResults;
  loading: boolean;
}

const initialState: SearchState = {
  query: '',
  filters: [],
  results: {processes: [], artifacts: []},
  loading: false,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setSearchFilters: (state, action: PayloadAction<Array<DocumentCategories>>) => {
      state.filters = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<SearchResults>) => {
      state.results = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(searchApi.endpoints.searchByKeyword.matchPending, (state, {payload}) => {
      console.log('Search API query pending: ' + JSON.stringify(payload));
      state.loading = true;
    });
    builder.addMatcher(searchApi.endpoints.searchByKeyword.matchFulfilled, (state, {payload}) => {
      console.log('Search API query result: ' + JSON.stringify(payload));
      state.results = payload;
      state.loading = false;
    });
  },
});

export const {setSearchQuery, setSearchResults, setSearchFilters, setLoading} = searchSlice.actions;

export const selectSearchQuery = (state: RootState) => state.search.query;
export const selectSearchResults = (state: RootState) => state.search.results;
export const selectSearchFilter = (state: RootState) => state.search.filters;
export const selectSearchLoading = (state: RootState) => state.search.loading;

export default searchSlice.reducer;

// export const searchAsync =
//   (query: string): AppThunk =>
//   async (dispatch) => {
//     try {
//       dispatch(setLoading(true));
//       console.log('Initiating search query: ' + query);
//       // Simulating an API call or search logic
//       // Replace this with your actual search implementation
//       const results: SearchResults = {
//         artifacts: [
//           {
//             id: 125,
//             key: 'FORM-0005092',
//             docid: 'FORM-0005092',
//             title:
//               'Global Regulatory Affairs Notification of Heath Authority Approvals of Investigator Brochure Reference Safety Information Updates',
//             type: 'Form',
//             url: 'http://dummyimage.com/235x100.png/5fa2dd/ffffff',
//             system: 'MEDIVA',
//             owner: null,
//             created_dt: '2023-05-18T03:09:37.383Z',
//           },
//           {
//             id: 126,
//             key: 'PROC-0002803',
//             docid: 'PROC-0002803',
//             title: "Global SOP Investigator's Brochure",
//             type: 'SOP',
//             url: 'http://dummyimage.com/102x100.png/cc0000/ffffff',
//             system: 'VeevaVault',
//             owner: 'Dale Goad',
//             created_dt: '2023-05-18T03:09:37.522Z',
//           },
//           {
//             id: 130,
//             key: null,
//             docid: null,
//             title: 'Investigator Brochure (IB) Distribution and Management_RSI IB CRO Memo',
//             type: 'Guidance Documents',
//             url: 'http://dummyimage.com/201x100.png/dddddd/000000',
//             system: 'Elevate',
//             owner: 'rebekah.gorney@takeda.com',
//             created_dt: '2023-05-18T03:09:38.204Z',
//           },
//           {
//             id: 133,
//             key: 'TOOL-0001568',
//             docid: 'TOOL-0001568',
//             title: 'Investigator’s Brochure Job Aid (R&D and PDT)',
//             type: 'Tool',
//             url: 'http://dummyimage.com/154x100.png/dddddd/000000',
//             system: 'MEDIVA',
//             owner: null,
//             created_dt: '2023-05-18T03:09:38.613Z',
//           },
//         ],
//       };

//       dispatch(setSearchResults(results));
//     } catch (error) {
//       console.error('Error occurred while searching:', error);
//     } finally {
//       dispatch(setLoading(false));
//     }
//   };
